import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import './Simulation.scss'
import NavigationBar from './NavigationBar'
import UnityComponent from './UnityComponent'
import LearningPanel from './learninglab/LearningPanel'
import HallwayContent from './learninglab/HallwayContent'

import { useLearningLab } from '../contexts/LearningLabContext'

import { FaultMode, SceneRoom, Lesson, SafetyException } from '../Types'
import { ModuleClass } from '../ModuleTypes'
import { storeInSession, sapphireConsoleCSS as logCSS, removeFromSession, removeBodyClass } from '../Utils'
import { useModuleContext } from '../contexts/ModuleContext'
import * as Solver from '../Solver'

import * as LearningLab from './learninglab/LearningLab'
import { isDebug } from '../App.config'

import Dialog from './Dialog'
import { simMod, moduleContent, getFaultMessage } from '../Module.config'

type Props = {
    faultMode?: FaultMode
}

const Simulation: React.FC<Props> = ({ faultMode }: Props) => {
    const { faultID, connection, groupName, user } = useModuleContext()
    const { state, dispatch } = useLearningLab()

    const [simIsLoaded, setSimIsLoaded] = useState(false)
    const [cost, setCost] = useState('0000.00')
    const [timeSpent, setTimeSpent] = useState('00:00')

    const [currentSceneRoom, setCurrentSceneRoom] = useState(SceneRoom.Default)
    const [showPopup, setShowPopup] = useState(true)
    const [goingToPostAssessment, setGoingToPostAssessment] = useState(false)

    const history = useHistory()

    const unityComponent = useRef<UnityComponent>()
    const lockedToBench = useRef(false)

    const lockToBench = useCallback((benchID: string) => {
        const regEx = new RegExp('^B0[1-9]$')
        if (!regEx.test(benchID)) throw new Error(`Invalid Bench ID '${benchID}'! Bench ID must be between 'B01' and 'B09'.`)
        isDebug && console.log(`%cTelling Unity to lock to bench '${benchID}'`, logCSS)
        unityComponent.current && unityComponent.current.unityContent.send('RoomManager', 'UnlockBench', benchID)
        lockedToBench.current = true
    }, [])

    const unlockBenches = useCallback(() => {
        isDebug && console.log(`%cTelling Unity to unlock all benches...`, logCSS)
        unityComponent.current && unityComponent.current.unityContent.send('RoomManager', 'UnlockBench', 'B00')
        lockedToBench.current = false
    }, [])

    const navigateToRoom = useCallback((sceneRoom: SceneRoom) => {
        if (!simIsLoaded || currentSceneRoom === sceneRoom || !unityComponent.current) return

        isDebug && console.log(`%cTelling Unity to enter ${sceneRoom} while in ${currentSceneRoom}...`, logCSS)
        switch (sceneRoom) {
            case SceneRoom.Hallway:
                unityComponent.current.unityContent.send('RoomManager', 'ReturnToHallway')
                break
            case SceneRoom.Lab:
                unityComponent.current.unityContent.send('RoomManager', 'EnterLab')
                break
            case SceneRoom.LabOverview:
                unityComponent.current.unityContent.send('RoomManager', 'LabOverview')
                lockedToBench.current && unlockBenches()
                break
            default:
                throw new Error(`Room navigation not implemented for ${sceneRoom}.`)
        }
    }, [simIsLoaded, currentSceneRoom, unlockBenches])

    const handleLesson = useCallback((lesson: Lesson) => {
        isDebug && console.log('Handling loading lesson...')
        if (lesson.faultID) storeInSession('faultId', lesson.faultID)
        currentSceneRoom === SceneRoom.Lab && navigateToRoom(SceneRoom.LabOverview)
        if (currentSceneRoom !== SceneRoom.Default) {
            lesson.benchID
                ? lockToBench(lesson.benchID)
                : lockedToBench.current && unlockBenches()
        }
    }, [currentSceneRoom, lockToBench, navigateToRoom, unlockBenches])

    useEffect(() => {
        if (!simMod.isLab || !simIsLoaded || state.currentSectionId === 0) return
        handleLesson(LearningLab.lesson(state.currentSectionId, state.currentLessonId))
    }, [handleLesson, state.currentSectionId, state.currentLessonId, simIsLoaded])

    const setSceneRoom = (room: SceneRoom) => {
        if (room === currentSceneRoom) return
        isDebug && console.log(`%cSave scene room '${room}' in state.`, logCSS)
        setCurrentSceneRoom(room)
    }

    const goToPostAssessment = () => {
        if (simMod.isLab) throw new Error('Attempted to got to Post Assessment when not in a Troubleshooting module.')
        setGoingToPostAssessment(true)
        history.push('/postassessment')
    }

    const handleExit = () => {
        if (currentSceneRoom === SceneRoom.Lab) {
            navigateToRoom(SceneRoom.Hallway)
            return
        }

        if (faultMode && faultMode === FaultMode.Troubleshoot) {
            goToPostAssessment()
            return
        }
        history.push('/')
    }

    const loadFault = async (fault: string) => {
        if (!fault) return
        isDebug && console.log(`%cSFI: Applying fault ${fault} in ${currentSceneRoom}`, logCSS)
        await Solver.loadFault(connection, groupName, state.currentLab, fault, user.electricalCode)
            .catch((error: any) => {
                console.error('Error. Solver could not load fault:', error)
            })
        isDebug && console.log(`%cSFI: Current Scene: ${state.currentLab}`, logCSS)
    }

    const handleSafetyExceptionDialogClose = (safetyType: SafetyException) => {
        if (safetyType !== SafetyException.Zap && safetyType !== SafetyException.Stop) return

        if (simMod.type === ModuleClass.LL) {
            navigateToRoom(SceneRoom.Hallway)
            return
        }
        if (faultMode === FaultMode.Explore) {
            history.push('/')
            return
        }
        goToPostAssessment()
    }

    const handleUnload = useCallback(() => {
        if (goingToPostAssessment) return // Don't complete fault if the component is unloading due to navigation to Post Assessment. Let Post Assessment page handle it.
        isDebug && console.log('%cSimulation component handling fault on unload...', logCSS)
        const re = new RegExp('F(00[1-9]|0[1-9][0-9]|[1-9][0-9][0-9]|[A-Z]{3})')
        if (re.test(faultID)) Solver.completeFault(faultID, connection, groupName)
    }, [connection, faultID, goingToPostAssessment, groupName])

    useEffect(() => {
        isDebug && console.log('Adding unload event listener...')
        window.addEventListener('unload', handleUnload)

        return () => {
            isDebug && console.log('%cSimulation component unmounting. ...', logCSS)
            window.removeEventListener('unload', handleUnload)
        }
    }, [handleUnload])

    useEffect(() => {
        isDebug && console.log('%cSimulation is mounted.', logCSS)
        if (faultMode) removeFromSession('faultAttempt') // Only do this if in Explore or Troubleshoot        
    }, [faultMode])

    const sectionName = simMod.isLab && state.currentSectionId > 0 ? LearningLab.section(state.currentSectionId).name : ''

    const pageTitle = simMod.isLab ? sectionName : moduleContent.title
    const faultMessage = getFaultMessage(faultMode)
    removeBodyClass(simMod.type)
    isDebug && console.log('Simulation rendering...')
    return (
        <>
            <div className="webgl-container">
                <div className="unityContainer">
                    <UnityComponent
                        faultMode={faultMode}
                        updateTime={setTimeSpent}
                        updateCost={setCost}
                        setStatus={setSimIsLoaded}
                        setSceneRoom={setSceneRoom}
                        unityRef={(ref: UnityComponent) => (unityComponent.current = ref)}
                        loadFault={loadFault}
                        onZapandStop={handleSafetyExceptionDialogClose}
                        currentLab={state.currentLab}
                        labDispatch={dispatch}
                    />
                </div>
                <NavigationBar
                    currentSceneRoom={currentSceneRoom}
                    dollarsSpent={cost}
                    timeSpent={timeSpent}
                    faultMode={faultMode}
                    pageTitle={pageTitle}
                    simIsLoaded={simIsLoaded}
                    onConfirmExit={handleExit}
                    isHomeAvailable={faultMode && faultMode === FaultMode.Troubleshoot ? false : true}
                />
                {
                    simIsLoaded
                    && simMod.isLab
                    && currentSceneRoom === SceneRoom.Lab
                    && <LearningPanel navigateToRoom={navigateToRoom} />

                }
                {
                    simIsLoaded
                    && simMod.isLab
                    && currentSceneRoom === SceneRoom.Hallway
                    && <HallwayContent navigateToRoom={navigateToRoom} />

                }
            </div>

            <Dialog
                title={'Attention'}
                body={faultMessage}
                show={simIsLoaded && faultMessage !== '' && showPopup}
                onHide={() => setShowPopup(false)}
            />
        </>
    )
}

export default Simulation

